import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { UPGRADE_ROUTES } from '@/api/constants/app.url';
import { useAccess } from '@/shared/access/use-access';
import { usePermissions } from '@/shared/access/use-permissions';
import { useUpgrade } from '@/shared/access/upgrade-context';
import { NotFoundPage } from '@/modules/dashboard/pages/not-found-page';
import type { AccessKey } from '@/shared/permissions';

/**
 * Route guard for feature-gated pages. When the member lacks the feature:
 *
 *   - Plan block (owner on a plan that doesn't include the module) → the shared
 *     `/upgrade` page with context about what was blocked.
 *   - Permission block (managed member without the grant) → `redirectTo` plus
 *     the shared Upgrade popup.
 *
 * Mount inside the dashboard layout so UpgradeProvider survives permission redirects.
 */
export function FeatureRoute({
    feature,
    redirectTo,
    requiresGrant = false,
    children,
}: {
    feature: AccessKey;
    redirectTo: string;
    /**
     * Require an EXPLICIT Role Matrix grant — owners do NOT pass for free.
     * Mirrors `requiresGrant` on the sidebar nav item, so a module hidden from
     * the menu can't be reached by typing its URL either. Keep the two in sync.
     */
    requiresGrant?: boolean;
    children: ReactNode;
}) {
    const { can } = useAccess();
    const permissions = usePermissions();
    const { requestUpgrade } = useUpgrade();
    // Owners carry a null permission map (full access); a `requiresGrant`
    // module must still find the key in it.
    const deniedByGrant = requiresGrant && !permissions?.[feature];
    const result = deniedByGrant
        ? { allowed: false, reason: 'permission' as const }
        : can(feature);

    useEffect(() => {
        // A `requiresGrant` denial is NOT an upsell: the module isn't part of
        // this workspace at all, so no plan can unlock it. Showing the Upgrade
        // popup there would be misleading — it 404s instead (see below).
        if (!result.allowed && result.reason !== 'plan' && !deniedByGrant) {
            requestUpgrade({ feature, reason: result.reason });
        }
    }, [result.allowed, result.reason, deniedByGrant, feature, requestUpgrade]);

    // The module doesn't exist for this workspace — treat the URL like any
    // other unknown route rather than hinting that the page is there.
    if (deniedByGrant) return <NotFoundPage />;

    if (!result.allowed) {
        const destination = result.reason === 'plan'
            ? UPGRADE_ROUTES.withContext(feature, result.reason)
            : redirectTo;
        return <Navigate to={destination} replace />;
    }
    return <>{children}</>;
}
